import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  addDoc,
  updateDoc,
  DocumentSnapshot,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { firestore } from '../utils/firebaseSetUp';
import {
  Organization,
  OrganizationCreate,
  OrganizationQueryResult,
  DEFAULT_ORGANIZATION_SETTINGS,
  DEFAULT_PLAN,
  OrganizationSettings,
} from '../models';
import { COLLECTIONS } from '../utils/collections';
import { isSelfHosted } from '../config/infra';
import { authenticatedHttpClient } from './httpClient';

// Re-export for backward compatibility
export type { Organization };

/**
 * Load organizations with pagination
 */
export const loadOrganizations = async (
  lastOrg?: DocumentSnapshot,
  limitCount: number = 10
): Promise<OrganizationQueryResult> => {
  let baseQuery = collection(firestore, COLLECTIONS.ORGANIZATIONS);

  let q = query(baseQuery, orderBy('created', 'desc'), limit(limitCount));

  if (lastOrg) {
    q = query(
      q,
      orderBy('created', 'desc'),
      startAfter(lastOrg),
      limit(limitCount)
    );
  }

  const snapshot = await getDocs(q);

  const organizations: Organization[] = [];
  snapshot.forEach(doc => {
    const data = doc.data();
    organizations.push({
      ...(data as Omit<Organization, 'id'>),
      id: doc.id,
    });
  });

  return {
    organizations,
    lastDoc:
      snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
    hasMore: snapshot.docs.length >= limitCount,
    totalCount: organizations.length,
  };
};

/**
 * Get organization by ID
 */
export const getOrganization = async (
  organizationId: string
): Promise<Organization | null> => {
  try {
    if (isSelfHosted) {
      const data = await authenticatedHttpClient.get<Organization>(
        `/organizations/${organizationId}`
      );
      return data;
    }

    const docRef = doc(firestore, COLLECTIONS.ORGANIZATIONS, organizationId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        ...(data as Omit<Organization, 'id'>),
        id: docSnap.id,
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting organization:', error);
    throw error;
  }
};

/**
 * Get the first organization (for default selection)
 */
export const getDefaultOrganization =
  async (): Promise<Organization | null> => {
    try {
      const result = await loadOrganizations(undefined, 1);
      return result.organizations.length > 0 ? result.organizations[0] : null;
    } catch (error) {
      console.error('Error getting default organization:', error);
      return null;
    }
  };

/**
 * Get multiple organizations by their IDs
 */
export const getOrganizationsByIds = async (
  organizationIds: string[]
): Promise<Organization[]> => {
  try {
    const organizations: Organization[] = [];

    for (const orgId of organizationIds) {
      const org = await getOrganization(orgId);
      if (org) {
        organizations.push(org);
      }
    }

    return organizations;
  } catch (error) {
    console.error('Error getting organizations by IDs:', error);
    throw error;
  }
};

/**
 * Create a new organization
 */
export const createOrganization = async (
  organizationData: OrganizationCreate
): Promise<string> => {
  try {
    const now = new Date();
    const organizationWithDefaults = {
      ...organizationData,
      settings: {
        ...DEFAULT_ORGANIZATION_SETTINGS,
        ...organizationData.settings,
      },
      plan: {
        ...DEFAULT_PLAN,
        ...organizationData.plan,
      },
      billing: organizationData.billing,
      active: true,
      created: now,
      updated: now,
    };

    const docRef = await addDoc(
      collection(firestore, COLLECTIONS.ORGANIZATIONS),
      organizationWithDefaults
    );

    return docRef.id;
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

/**
 * Update organization settings with currency change validation
 */
export const updateOrganization = async (
  organizationId: string,
  data: Partial<Organization>
): Promise<void> => {
  try {
    await authenticatedHttpClient.put<Organization>(
      `/organizations/${organizationId}`,
      data
    );
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error;
  }
};

/**
 * Update organization settings with currency change validation
 */
export const updateOrganizationSettings = async (
  organizationId: string,
  settings: Partial<OrganizationSettings>
): Promise<void> => {
  try {
    // Update settings
    const docRef = doc(firestore, COLLECTIONS.ORGANIZATIONS, organizationId);
    await updateDoc(docRef, {
      settings: settings,
      updated: new Date(),
    });

    console.log(`Organization settings updated for ${organizationId}`);
  } catch (error) {
    console.error('Error updating organization settings:', error);
    throw error;
  }
};
